import type { ChangeEvent } from 'react';
import type { FC } from '../../lib/teact/teact';
import React, { useState } from '../../lib/teact/teact';

import { loginSeller, registerSeller, SellerApiError } from '../../api/services/sellerAuth';
import { setPendingCredentials, setSellerSession } from '../../util/sellerSession';

import useFlag from '../../hooks/useFlag';
import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import './SellerAuth.scss';

type Mode = 'login' | 'register';

const SellerAuth: FC = () => {
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordVisible, markPasswordVisible, unmarkPasswordVisible] = useFlag();
  const [isConfirmVisible, markConfirmVisible, unmarkConfirmVisible] = useFlag();

  const canSubmitLogin = username.trim().length > 0 && password.length >= 6;
  const canSubmitRegister = (
    username.trim().length > 0
    && telegramUsername.trim().length > 0
    && password.length >= 6
    && confirmPassword.length >= 6
  );
  const canSubmit = mode === 'login' ? canSubmitLogin : canSubmitRegister;

  const handleUsernameChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setUsername(e.currentTarget.value);
  });

  const handleTelegramUsernameChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setTelegramUsername(e.currentTarget.value);
  });

  const handlePasswordChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setPassword(e.currentTarget.value);
  });

  const handleConfirmPasswordChange = useLastCallback((e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setConfirmPassword(e.currentTarget.value);
  });

  const goPending = useLastCallback((cleanUsername: string, pwd: string) => {
    setPendingCredentials(cleanUsername, pwd);
    window.location.reload();
  });

  const handleSubmit = useLastCallback(async (e: Event) => {
    e.preventDefault();
    if (!canSubmit || isLoading) return;

    const cleanUsername = username.trim().replace(/^@/, '');
    const cleanTelegramUsername = telegramUsername.trim().replace(/^@/, '');

    if (mode === 'register' && password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      if (mode === 'register') {
        await registerSeller({
          username: cleanUsername,
          password,
          telegramUsername: cleanTelegramUsername,
        });
        goPending(cleanUsername, password);
        return;
      }

      const result = await loginSeller({
        username: cleanUsername,
        password,
      });
      setSellerSession({
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        name: result.seller.name,
      });
      window.location.reload();
    } catch (err) {
      if (err instanceof SellerApiError) {
        if (err.code === 'SELLER_PENDING_APPROVAL') {
          goPending(cleanUsername, password);
          return;
        }
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Что-то пошло не так');
      }
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <div className="SellerGate custom-scroll">
      <div className="SellerGate__card">
        <div className="SellerGate__brand">
          <div className="SellerGate__mark">MyZakaz</div>
        </div>

        <h1 className="SellerGate__title">
          {mode === 'login' ? 'Вход' : 'Регистрация'}
        </h1>
        <p className="SellerGate__lede">
          {mode === 'login'
            ? 'Войдите в кабинет продавца, чтобы работать с заказами.'
            : 'Создайте аккаунт. После регистрации откроется комната ожидания, пока вас одобрят.'}
        </p>

        <form className="SellerGate__form" action="" onSubmit={handleSubmit} autoComplete="on">
          {error && (
            <div className="SellerGate__error" role="alert">
              <span className="SellerGate__errorIcon" aria-hidden>!</span>
              <span>{error}</span>
            </div>
          )}

          <label className="SellerGate__field" htmlFor="seller-username">
            <span className="SellerGate__label">Логин</span>
            <input
              id="seller-username"
              className="SellerGate__input"
              value={username}
              autoComplete="username"
              placeholder="логин"
              onChange={handleUsernameChange}
            />
          </label>

          {mode === 'register' && (
            <label className="SellerGate__field" htmlFor="seller-telegram-username">
              <span className="SellerGate__label">Telegram</span>
              <input
                id="seller-telegram-username"
                className="SellerGate__input"
                value={telegramUsername}
                autoComplete="off"
                placeholder="@username"
                onChange={handleTelegramUsernameChange}
              />
            </label>
          )}

          <label className="SellerGate__field" htmlFor="seller-password">
            <span className="SellerGate__label">Пароль</span>
            <div className="SellerGate__inputWrap">
              <input
                id="seller-password"
                className="SellerGate__input SellerGate__input--password"
                type={isPasswordVisible ? 'text' : 'password'}
                value={password}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                placeholder="минимум 6 символов"
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="SellerGate__eye"
                onClick={isPasswordVisible ? unmarkPasswordVisible : markPasswordVisible}
                title="Показать пароль"
                aria-label="Показать пароль"
              >
                <Icon name={isPasswordVisible ? 'eye' : 'eye-crossed'} />
              </button>
            </div>
          </label>

          {mode === 'register' && (
            <label className="SellerGate__field" htmlFor="seller-confirm-password">
              <span className="SellerGate__label">Повторите пароль</span>
              <div className="SellerGate__inputWrap">
                <input
                  id="seller-confirm-password"
                  className="SellerGate__input SellerGate__input--password"
                  type={isConfirmVisible ? 'text' : 'password'}
                  value={confirmPassword}
                  autoComplete="new-password"
                  placeholder="ещё раз"
                  onChange={handleConfirmPasswordChange}
                />
                <button
                  type="button"
                  className="SellerGate__eye"
                  onClick={isConfirmVisible ? unmarkConfirmVisible : markConfirmVisible}
                  title="Показать пароль"
                  aria-label="Показать пароль"
                >
                  <Icon name={isConfirmVisible ? 'eye' : 'eye-crossed'} />
                </button>
              </div>
            </label>
          )}

          <button
            type="submit"
            className="SellerGate__submit"
            disabled={!canSubmit || isLoading}
          >
            {isLoading
              ? (mode === 'login' ? 'Входим…' : 'Создаём…')
              : (mode === 'login' ? 'Войти' : 'Создать аккаунт')}
          </button>

          <div className="SellerGate__switch">
            <span>
              {mode === 'login' ? 'Нет аккаунта?' : 'Уже есть аккаунт?'}
            </span>
            <button
              type="button"
              className="SellerGate__link"
              onClick={() => {
                setError(null);
                setConfirmPassword('');
                setMode(mode === 'login' ? 'register' : 'login');
              }}
            >
              {mode === 'login' ? 'Зарегистрироваться' : 'Войти'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellerAuth;
